import { Button, Dropdown, IconButton, useToast } from '@hkdevs/design-system';
import { useState } from 'react';
import { useExportOrgStructure } from '../../api/queries/orgStructure';
import { PageLayout } from '../../shell/Page';
import { downloadBlob } from '../payHistory/csv';
import { ImportOrgDrawer } from './ImportOrgDrawer';
import { UsersView, type UsersSearch } from './UsersView';
import { withReference } from '../../api/withReference';

/**
 * The Users page: the header's Create user and its overflow (export and import the org
 * structure), over the users list.
 */
export function UsersPage({
  search,
  onSearchChange,
}: {
  search: UsersSearch;
  onSearchChange: (next: UsersSearch) => void;
}) {
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  // A new key each time the drawer opens, so it starts at step 1 with no file.
  const [importKey, setImportKey] = useState<number | null>(null);
  const exportFile = useExportOrgStructure();

  const download = () =>
    exportFile.mutate(undefined, {
      onSuccess: ({ blob, fileName }) => downloadBlob(blob, fileName),
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: 'The org structure couldn’t be exported',
              description: 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });

  return (
    <PageLayout
      page="users"
      actions={
        <>
          <Button size="sm" leadingIcon="plus" onClick={() => setCreating(true)}>
            Create user
          </Button>
          <Dropdown
            align="end"
            trigger={
              <IconButton
                icon="more-vertical"
                size="sm"
                variant="secondary"
                aria-label="More actions"
              />
            }
            items={[
              { label: 'Export org structure', icon: 'download', onClick: download },
              {
                label: 'Import org structure',
                icon: 'upload',
                onClick: () => setImportKey(Date.now()),
              },
            ]}
          />
        </>
      }
    >
      <UsersView
        search={search}
        onSearchChange={onSearchChange}
        creating={creating}
        onCreatingChange={setCreating}
      />
      {importKey !== null && (
        <ImportOrgDrawer
          key={importKey}
          open
          onClose={() => setImportKey(null)}
          onDownloadCurrent={download}
          downloading={exportFile.isPending}
        />
      )}
    </PageLayout>
  );
}
